// src/components/ProtectedRoute.jsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react'; // Or your preferred loader
import PageLayout from './PageLayout';

export function ProtectedRoute({ children, adminOnly = false }) {
  const { currentUser, currentUserProfile, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    // Show a loading indicator while checking auth state & profile
    return (
      <PageLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!currentUser) {
    // User not logged in, redirect them to the login page (or home)
    // Pass the current location so we can redirect back after login
    console.log('ProtectedRoute: Not logged in, redirecting to login.');
    // Replace '/' with your actual login route if different
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (!currentUserProfile) {
     // Logged in, but profile hasn't loaded yet (edge case after context loaded)
     // Or profile doesn't exist (indicates an issue)
     console.warn('ProtectedRoute: User logged in but profile not available.');
     // You might show a specific message or redirect, here we block access
      return (
          <PageLayout>
              <div className="flex flex-col justify-center items-center min-h-screen text-center p-4">
                  <h2 className="text-xl font-semibold mb-2">Profile Not Loaded</h2>
                  <p className="text-muted-foreground">Your profile data could not be loaded. Please try again later or contact support.</p>
                  {/* Optionally add a logout button */}
              </div>
          </PageLayout>
      );
  }

  // Check if user is approved
  if (currentUserProfile.approvalStatus !== 'approved') {
    console.log(`ProtectedRoute: User status is ${currentUserProfile.approvalStatus}. Access denied.`);
    // For non-approved users, only allow access to profile page
    if (location.pathname === '/profile') {
      return children;
    }
    return (
      <PageLayout>
        <div className="flex flex-col justify-center items-center min-h-[calc(100vh-4rem)] text-center p-4">
          <h2 className="text-xl font-semibold mb-2">Access Pending or Denied</h2>
          <p className="text-muted-foreground">
            {currentUserProfile.approvalStatus === 'pending'
              ? 'Your account is awaiting admin approval.'
              : 'Your access request has been rejected.'}
            {' Please contact support if you have questions.'}
          </p>
        </div>
      </PageLayout>
    );
  }

   if (adminOnly && currentUserProfile.role !== 'admin') {
        // User is approved but not an admin, trying to access admin-only route
        console.log(`ProtectedRoute: User role is ${currentUserProfile.role}. Admin access denied.`);
        // Redirect to home or a generic "Access Denied" page
        return <Navigate to="/" state={{ from: location }} replace />;
    }


  // User is logged in, profile loaded, approved, and has necessary role (if applicable)
  return children;
}